import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Wallet,
  LogOut,
  Truck,
} from 'lucide-react';
import { useKeycloak } from '../../auth/keycloakProvider';
import { useWallet } from '../../contexts/WalletContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '../../components/ui/sidebar';

const adminNavItems = [
  { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
  { title: 'Empresas', url: '/companies', icon: Building2 },
  { title: 'Colaboradores', url: '/employees', icon: Users },
  { title: 'eCMRs', url: '/ecmrs', icon: FileText },
];

const companyManagerNavItems = [
  { title: 'Empresas', url: '/companies', icon: Building2 },
  { title: 'eCMRs', url: '/ecmrs', icon: FileText },
];

const employeeNavItems = [
  { title: 'eCMRs', url: '/ecmrs', icon: FileText },
];

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSystemAdmin, isCompanyManager, isCompanyAdministrator } = useKeycloak();
  const { isUnlocked, lock, setShowUnlockModal } = useWallet();
  const { state } = useSidebar();
  
  const collapsed = state === 'collapsed';
  const navItems = isSystemAdmin 
    ? adminNavItems 
    : isCompanyManager || isCompanyAdministrator
    ? companyManagerNavItems 
    : employeeNavItems;

  const handleLogout = () => {
    lock();
    logout();
    navigate('/login');
  };

  const handleWalletClick = () => {
    if (isUnlocked) {
      lock();
    } else {
      setShowUnlockModal(true);
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
            <Truck className="h-5 w-5 text-accent-foreground" />
          </div>
          {!collapsed && (
            <div className="flex flex-col animate-fade-in">
              <span className="font-semibold text-sidebar-foreground">Nexus.eCMR</span>
              <span className="text-xs text-sidebar-muted">Document Manager</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="text-sidebar-muted text-xs uppercase tracking-wider mb-2">
              Navegação
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const isActive = location.pathname === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={item.title}
                      className={cn(
                        "transition-all duration-200",
                        isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
                      )}
                    >
                      <button onClick={() => navigate(item.url)} className="w-full">
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-auto">
          {!collapsed && (
            <SidebarGroupLabel className="text-sidebar-muted text-xs uppercase tracking-wider mb-2">
              Segurança
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleWalletClick}
                  tooltip="Wallet"
                  className={cn(
                    "transition-all duration-200",
                    isUnlocked && "text-accent"
                  )}
                >
                  <Wallet className={cn("h-4 w-4", isUnlocked && "text-accent")} />
                  <span>Wallet {isUnlocked ? '(Desbloqueada)' : '(Bloqueada)'}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        {!collapsed && user && (
          <div className="mb-3 animate-fade-in">
            <p className="text-sm font-medium text-sidebar-foreground">{user.name}</p>
            <p className="text-xs text-sidebar-muted">
              {isSystemAdmin ? 'System Admin' : user.companyName}
            </p>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'sm'}
            onClick={handleLogout}
            className="w-full text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && <span>Sair</span>}
          </Button>
          <SidebarTrigger className="text-sidebar-muted hover:text-sidebar-foreground" />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
